import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  TrendingUp, Wallet, Banknote, RotateCcw, Route, Download, RefreshCw, Loader2,
} from "lucide-react";
import { adminApi, formatApiError } from "./api";
import { PageHeader } from "./shared";

const money = (n) => `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PERIODS = [["7d", "7 days"], ["30d", "1 month"], ["1y", "1 year"]];

const Section = ({ icon: Icon, title, tint, children, testid }) => (
  <section className="rounded-2xl border border-[#12142b]/8 bg-white shadow-sm" data-testid={testid}>
    <div className="flex items-center gap-2 border-b border-[#12142b]/8 px-6 py-4">
      <span className="grid h-9 w-9 place-items-center rounded-xl" style={{ backgroundColor: `${tint}14` }}>
        <Icon className="h-4.5 w-4.5" style={{ color: tint }} strokeWidth={1.75} />
      </span>
      <h2 className="font-display text-lg font-bold text-[#12142b]">{title}</h2>
    </div>
    <div className="p-6 pt-4">{children}</div>
  </section>
);

const Row = ({ label, value, strong, sub, accent }) => (
  <div className="flex items-center justify-between border-b border-[#12142b]/6 py-2.5 text-sm last:border-0">
    <span className={strong ? "font-bold text-[#12142b]" : "text-[#565b73]"}>{label}{sub && <span className="ml-1.5 text-xs text-[#565b73]/70">{sub}</span>}</span>
    <span className="font-semibold" style={{ color: accent || "#12142b" }}>{value}</span>
  </div>
);

export default function FullReport() {
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    setError("");
    adminApi.get("/finance/full-report", { params: { period } })
      .then((r) => setData(r.data))
      .catch((e) => {
        setData(null);
        setError(formatApiError(e.response?.data?.detail) || "Could not load the report");
      });
  }, [period]);
  useEffect(() => { setData(null); load(); }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await adminApi.get("/finance/full-report.csv", { params: { period }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `saferide-full-report-${period}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded as CSV");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not export the report");
    } finally {
      setExporting(false);
    }
  };

  const pr = data?.platform_revenue;
  const de = data?.driver_earnings;
  const po = data?.payouts;
  const rf = data?.refunds;
  const ts = data?.trip_summary;

  return (
    <div data-testid="full-report-page">
      <PageHeader title="Full Financial Report" subtitle="Platform revenue, driver earnings, payouts, refunds and trip activity for the selected period."
        right={
          <div className="flex items-center gap-3">
            <div className="flex gap-1 rounded-lg bg-[#f4f6fb] p-1" data-testid="report-period-toggle">
              {PERIODS.map(([k, l]) => (
                <button key={k} data-testid={`report-period-${k}`} onClick={() => setPeriod(k)}
                  className={`rounded-md px-3.5 py-1.5 text-xs font-semibold transition-colors ${period === k ? "bg-white text-[#2340b5] shadow-sm" : "text-[#565b73]"}`}>{l}</button>
              ))}
            </div>
            <button data-testid="report-export-csv" onClick={exportCsv} disabled={!data || exporting}
              className="inline-flex items-center gap-2 rounded-xl bg-[#2340b5] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#1b3390] disabled:opacity-60">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export CSV
            </button>
          </div>} />

      <div className="space-y-6 p-8">
        {error && (
          <div className="rounded-2xl border border-[#e4002b]/20 bg-[#e4002b]/5 p-5 text-center" data-testid="report-error">
            <p className="text-sm font-semibold text-[#e4002b]">{error}</p>
            <button data-testid="report-retry" onClick={load}
              className="mt-3 rounded-xl bg-[#e4002b] px-4 py-2 text-sm font-bold text-white hover:bg-[#c40024]">Retry</button>
          </div>
        )}
        {!data && !error && <p className="py-10 text-center text-sm text-[#565b73]">Loading report…</p>}

        {data && (
          <>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Platform Revenue */}
              <Section icon={TrendingUp} title="Platform Revenue" tint="#2340b5" testid="report-platform-revenue">
                {pr ? (
                  <>
                    <Row label="Booking fees" value={money(pr.booking_fees)} />
                    <Row label="E-transfer fees" value={money(pr.etransfer_fees)} />
                    <Row label="Ad fees" value={money(pr.ad_fees)} />
                    <Row label="Pin fees" value={money(pr.pin_fees)} />
                    <Row label="Total platform fees" value={money(pr.total_fees)} strong />
                    <Row label="Refunded platform fees" value={money(pr.refunded_fees)} accent="#b45309" />
                    <div className="mt-3 rounded-xl bg-[#16a34a]/8 p-4 text-center">
                      <p className="font-display text-2xl font-black tracking-tight text-[#16a34a]" data-testid="report-net-profit">{money(pr.net_profit)}</p>
                      <p className="text-xs font-semibold text-[#565b73]">Net platform profit · {PERIODS.find(([k]) => k === period)?.[1]}</p>
                    </div>
                    <p className="mt-3 text-xs text-[#565b73]/80">Only platform fees count as revenue — rider seat fares belong to drivers, and refunds return the riders' share (including the booking fee), so no platform fee is ever refunded.</p>
                  </>
                ) : <p className="text-sm text-[#565b73]">Available to super admins only.</p>}
              </Section>

              {/* Driver Earnings */}
              <Section icon={Wallet} title="Driver Earnings" tint="#16a34a" testid="report-driver-earnings">
                <Row label="Total driver earnings" value={money(de?.total)} strong />
                <Row label="Completed-trip bookings" value={de?.trips ?? 0} />
                <div className="mt-3 max-h-56 overflow-y-auto rounded-xl bg-[#f4f6fb] p-3" data-testid="report-driver-list">
                  {de?.drivers?.length ? (
                    <table className="w-full text-left text-sm">
                      <thead><tr className="text-xs uppercase tracking-wide text-[#565b73]">
                        <th className="pb-2 font-semibold">Driver</th>
                        <th className="pb-2 font-semibold">Trips</th>
                        <th className="pb-2 text-right font-semibold">Earned</th>
                      </tr></thead>
                      <tbody>
                        {de.drivers.map((d, i) => (
                          <tr key={d.name + i} data-testid={`report-driver-${i}`} className="border-t border-[#12142b]/6">
                            <td className="py-2 font-semibold text-[#12142b]">{d.name}</td>
                            <td className="py-2 text-[#565b73]">{d.trips}</td>
                            <td className="py-2 text-right font-semibold text-[#12142b]">{money(d.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : <p className="py-3 text-center text-sm text-[#565b73]">No completed trips in this period.</p>}
                </div>
              </Section>

              {/* Payouts */}
              <Section icon={Banknote} title="Payouts" tint="#7c3aed" testid="report-payouts">
                <Row label="Total paid out" value={money(po?.total)} strong />
                <Row label="Payouts processed" value={po?.count ?? 0} />
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {[["Stripe bank transfers", po?.stripe], ["Manual e-transfers", po?.etransfer], ["Settle & Pay", po?.settled]].map(([label, v], i) => (
                    <div key={label} className="rounded-xl bg-[#f4f6fb] p-3" data-testid={`report-payout-${["stripe", "etransfer", "settled"][i]}`}>
                      <p className="font-display text-lg font-black text-[#12142b]">{money(v?.amount)}</p>
                      <p className="text-xs font-medium text-[#565b73]">{label} · {v?.count ?? 0}</p>
                    </div>
                  ))}
                </div>
              </Section>

              {/* Refunds */}
              <Section icon={RotateCcw} title="Refunds" tint="#e4002b" testid="report-refunds">
                <Row label="Total refunded to riders" value={money(rf?.total)} strong />
                <Row label="Refunds issued" value={rf?.count ?? 0} />
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <div className="rounded-xl bg-[#f4f6fb] p-3" data-testid="report-refund-auto">
                    <p className="font-display text-lg font-black text-[#16a34a]">{money(rf?.auto?.amount)}</p>
                    <p className="text-xs font-medium text-[#565b73]">Auto-approved · {rf?.auto?.count ?? 0} (under threshold)</p>
                  </div>
                  <div className="rounded-xl bg-[#f4f6fb] p-3" data-testid="report-refund-admin">
                    <p className="font-display text-lg font-black text-[#b45309]">{money(rf?.admin?.amount)}</p>
                    <p className="text-xs font-medium text-[#565b73]">Admin-approved · {rf?.admin?.count ?? 0} (over threshold)</p>
                  </div>
                </div>
                <p className="mt-3 text-xs text-[#565b73]/80">Refunds return the riders' share of cancelled bookings — refunded platform fees: {money(rf?.fee_portion)}.</p>
              </Section>
            </div>

            {/* Trip Summary */}
            <Section icon={Route} title="Trip Summary" tint="#0e7490" testid="report-trip-summary">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[["Trips completed", ts?.completed, "#16a34a"], ["Trips cancelled", ts?.cancelled, "#e4002b"],
                  ["Auto-cancelled", ts?.auto_cancelled, "#b45309"], ["Bookings made", ts?.bookings, "#2340b5"]].map(([label, v, c], i) => (
                  <div key={label} className="rounded-xl bg-[#f4f6fb] p-4 text-center" data-testid={`report-trip-${["completed", "cancelled", "auto", "bookings"][i]}`}>
                    <p className="font-display text-2xl font-black tracking-tight" style={{ color: c }}>{v ?? 0}</p>
                    <p className="text-xs font-semibold text-[#565b73]">{label}</p>
                  </div>
                ))}
              </div>
            </Section>
          </>
        )}
      </div>
    </div>
  );
}
